import React, { useContext, useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Badge,
  Popover,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  ListItemButton,
  Divider,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  AccountCircle as AccountCircleIcon,
  Person as PersonIcon,
  Support as SupportIcon,
  Logout as LogoutIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../../context/UserProvider";
import useGetNotifications from "../../hooks/useGetNotifications";
import useCompanySettings from "../../hooks/useCompanySettings";

const AppBarComponent = ({
  isMobile,
  open,
  drawerWidth,
  collapsedWidth,
  handleDrawerToggle,
}) => {
  const { user } = useContext(UserContext);
  const { data, isLoading } = useGetNotifications();
  const navigate = useNavigate();
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const [notifAnchorEl, setNotifAnchorEl] = useState(null);

  const isProfileMenuOpen = Boolean(profileAnchorEl);
  const isNotifOpen = Boolean(notifAnchorEl);

  const handleProfileMenuOpen = (event) => {
    setProfileAnchorEl(event.currentTarget);
  };

  const handleNotifOpen = (event) => {
    setNotifAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileAnchorEl(null);
  };

  const handleNotifClose = () => {
    setNotifAnchorEl(null);
  };

  const handleLogout = () => {
    handleProfileMenuClose();
    navigate("/logout");
  };

  const handleProfileClick = () => {
    handleProfileMenuClose();
    navigate("/home/profile");
  };

  const handleSupportClick = () => {
    handleProfileMenuClose();
    navigate("/home/support");
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  };

  const profileMenuId = "primary-search-account-menu";
  const notifMenuId = "notifications-menu";

  const renderProfileMenu = (
    <Popover
      anchorEl={profileAnchorEl}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      id={profileMenuId}
      keepMounted
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      open={isProfileMenuOpen}
      onClose={handleProfileMenuClose}
      PaperProps={{ sx: { mt: 1, minWidth: 200 } }}
    >
      <List>
        <ListItemButton onClick={handleProfileClick}>
          <ListItemIcon>
            <PersonIcon />
          </ListItemIcon>
          <ListItemText primary="Profile" />
        </ListItemButton>
        <ListItemButton onClick={handleSupportClick}>
          <ListItemIcon>
            <SupportIcon />
          </ListItemIcon>
          <ListItemText primary="Support Request" />
        </ListItemButton>
        <ListItemButton onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText primary="Logout" />
        </ListItemButton>
      </List>
    </Popover>
  );

  const renderNotifications = (
    <Popover
      anchorEl={notifAnchorEl}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      id={notifMenuId}
      keepMounted
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      open={isNotifOpen}
      onClose={handleNotifClose}
      PaperProps={{
        sx: {
          mt: 1,
          width: { xs: "90vw", sm: 400 }, // Responsive width
          maxHeight: "70vh",
          overflowY: "auto",
        },
      }}
    >
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Notifications
        </Typography>
        {isLoading ? (
          <Typography variant="body2" color="text.secondary">
            Loading notifications...
          </Typography>
        ) : data && data.length > 0 ? (
          <List dense>
            {data.map((notification, index) => (
              <React.Fragment key={index}>
                <ListItem>
                  <ListItemText
                    primary={
                      <Typography variant="subtitle2">
                        {notification.title}
                      </Typography>
                    }
                    secondary={
                      <>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          component="span"
                        >
                          {notification.message}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ mt: 0.5 }}
                        >
                          {formatDate(notification.createdOn)} - From:{" "}
                          {notification.sender}
                        </Typography>
                      </>
                    }
                  />
                </ListItem>
                {index < data.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No new notifications
          </Typography>
        )}
      </Box>
    </Popover>
  );

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          width: `calc(100% - ${
            isMobile ? 0 : open ? drawerWidth : collapsedWidth
          }px)`,
          ml: isMobile ? 0 : `${open ? drawerWidth : collapsedWidth}px`,
          transition: (theme) =>
            theme.transitions.create(["width", "margin"], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
        }}
      >
        <Toolbar>
          {isMobile && (
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
              className="menu-toggle-button"
            >
              <MenuIcon />
            </IconButton>
          )}
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {user?.company}
          </Typography>

          <Box sx={{ display: "flex" }}>
            <IconButton
              size="large"
              aria-label="show notifications"
              color="inherit"
              onClick={handleNotifOpen}
            >
              <Badge badgeContent={data?.length || 0} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
            <IconButton
              size="large"
              edge="end"
              aria-label="account of current user"
              aria-controls={profileMenuId}
              aria-haspopup="true"
              onClick={handleProfileMenuOpen}
              color="inherit"
            >
              <AccountCircleIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>
      {renderProfileMenu}
      {renderNotifications}
    </>
  );
};

export default AppBarComponent;
